/**
 * NFT Trades API Client
 *
 * Handles API calls for peer-to-peer NFT trades.
 */

import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

export type TradeExpirationOption = '1_day' | '3_days' | '7_days' | '30_days';

export type TradeStatus =
  | 'proposed'
  | 'countered'
  | 'accepted'
  | 'signing'
  | 'settling'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'cancelled';

export type TradeSide = 'initiator' | 'recipient';

// websocketUrl stays required: SigningWizard passes it to a required prop.
export type XamanPayload = XamanPayloadResponse & { websocketUrl: string };

export interface TradeItem {
  id: string;
  trade_id: string;
  nft_token_id: string;
  side: TradeSide;
  version: number;
  sell_offer_index?: string;
  sell_offer_tx_hash?: string;
  transfer_offer_index?: string;
  status: 'pending' | 'offer_created' | 'transferred';
  created_at: string;
  // NFT details (joined)
  nft_name?: string | null;
  nft_image_url?: string | null;
  nft_thumbnail_url?: string | null;
  rarity_rank?: number | null;
  collection_id?: string | null;
  collection_name?: string | null;
  floor_price_drops?: string | null;
}

export interface TradeVersion {
  id: string;
  trade_id: string;
  version: number;
  proposed_by: string;
  message?: string | null;
  initiator_nft_token_ids: string[];
  recipient_nft_token_ids: string[];
  initiator_xrp_drops: string;
  recipient_xrp_drops: string;
  created_at: string;
}

export interface NFTTrade {
  id: string;
  initiator_address: string;
  recipient_address: string;
  status: TradeStatus;
  active_party: TradeSide;
  current_version: number;
  initiator_xrp_drops: string;
  recipient_xrp_drops: string;
  message?: string | null;
  expires_at?: string | null;
  fee_drops: string;
  signing_failure_count: number;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  initiator_signing_completed_at?: string | null;
  recipient_signing_completed_at?: string | null;
  created_at: string;
  updated_at: string;
  // Items (joined)
  initiator_items: TradeItem[];
  recipient_items: TradeItem[];
  initiator_item_count?: number;
  recipient_item_count?: number;
  // Version history (optional)
  versions?: TradeVersion[];
  // Computed by backend for pending tab
  is_your_turn?: boolean;
}

export interface TradeListResponse {
  trades: NFTTrade[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateTradeParams {
  recipientAddress: string;
  initiatorNftTokenIds: string[];
  recipientNftTokenIds: string[];
  initiatorXrpAmount?: number;
  recipientXrpAmount?: number;
  message?: string;
  expiration: TradeExpirationOption;
}

export interface CounterTradeParams {
  initiatorNftTokenIds: string[];
  recipientNftTokenIds: string[];
  initiatorXrpAmount?: number;
  recipientXrpAmount?: number;
  message?: string;
}

export interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

export interface SigningResponse {
  payloads: Array<{
    nftTokenId: string;
    payload?: XamanPayload | null; // present for Xaman sessions only
    signingIntent?: SigningIntentRef; // present always (multi-wallet)
  }>;
  escrowPayload?: XamanPayload | null;
  escrowSigningIntent?: SigningIntentRef | null;
  alreadySigned?: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new trade proposal
 */
export async function createTrade(params: CreateTradeParams): Promise<{ trade: NFTTrade }> {
  const response = await apiClient.post<ApiResponse<{ trade: NFTTrade }>>(
    '/trades/create',
    params
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create trade');
  }
  return response.data;
}

/**
 * Get trade details with version history
 */
export async function getTradeDetails(tradeId: string): Promise<{ trade: NFTTrade }> {
  const response = await apiClient.get<ApiResponse<{ trade: NFTTrade }>>(
    `/trades/${tradeId}`
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get trade details');
  }
  return response.data;
}

/**
 * List trades for the authenticated user
 */
export async function getTrades(
  direction: 'pending' | 'incoming' | 'outgoing' | 'history',
  options: { limit?: number; offset?: number } = {}
): Promise<TradeListResponse> {
  const params = new URLSearchParams();
  params.set('direction', direction);
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await apiClient.get<ApiResponse<TradeListResponse>>(
    `/trades?${params}`
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get trades');
  }
  return response.data;
}

/**
 * Get pending incoming trade count for badge
 */
export async function getPendingTradeCount(): Promise<{ count: number }> {
  const response = await apiClient.get<ApiResponse<{ count: number }>>(
    '/trades/pending-count'
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get pending count');
  }
  return response.data;
}

/**
 * Counter a trade
 */
export async function counterTrade(
  tradeId: string,
  params: CounterTradeParams
): Promise<{ trade: NFTTrade }> {
  const response = await apiClient.post<ApiResponse<{ trade: NFTTrade }>>(
    `/trades/${tradeId}/counter`,
    params
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to counter trade');
  }
  return response.data;
}

/**
 * Accept a trade
 */
export async function acceptTrade(tradeId: string): Promise<{ trade: NFTTrade }> {
  const response = await apiClient.post<ApiResponse<{ trade: NFTTrade }>>(
    `/trades/${tradeId}/accept`
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to accept trade');
  }
  return response.data;
}

/**
 * Decline a trade
 */
export async function declineTrade(tradeId: string): Promise<void> {
  const response = await apiClient.post<ApiResponse<void>>(
    `/trades/${tradeId}/decline`
  );
  if (!response.success) {
    throw new Error(response.error || 'Failed to decline trade');
  }
}

/**
 * Cancel a trade
 */
export async function cancelTrade(tradeId: string): Promise<void> {
  const response = await apiClient.post<ApiResponse<void>>(
    `/trades/${tradeId}/cancel`
  );
  if (!response.success) {
    throw new Error(response.error || 'Failed to cancel trade');
  }
}

/**
 * Start signing (either party - parallel signing)
 */
export async function startSigning(tradeId: string): Promise<SigningResponse> {
  const response = await apiClient.post<ApiResponse<SigningResponse>>(
    `/trades/${tradeId}/start-signing`
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to start signing');
  }
  return response.data;
}

/**
 * Confirm a signed sell offer for a trade item
 * offerIndex is optional - backend will look it up from txHash if not provided
 */
export async function confirmTradeOffer(
  tradeId: string,
  params: { nftTokenId: string; offerTxHash: string; offerIndex?: string }
): Promise<{ allConfirmed: boolean }> {
  const response = await apiClient.post<ApiResponse<{ allConfirmed: boolean }>>(
    `/trades/${tradeId}/confirm-offer`,
    params
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to confirm offer');
  }
  return response.data;
}

/**
 * Confirm escrow creation
 */
export async function confirmTradeEscrow(
  tradeId: string,
  params: { escrowTxHash: string; escrowSequence?: number }
): Promise<void> {
  const response = await apiClient.post<ApiResponse<void>>(
    `/trades/${tradeId}/confirm-escrow`,
    params
  );
  if (!response.success) {
    throw new Error(response.error || 'Failed to confirm escrow');
  }
}

/**
 * Check if an NFT is in an active trade
 */
export async function checkNFTTradeStatus(nftTokenId: string): Promise<{
  inTrade: boolean;
}> {
  const response = await apiClient.get<ApiResponse<{ inTrade: boolean }>>(
    `/trades/nft/${nftTokenId}/status`
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check NFT trade status');
  }
  return response.data;
}

/**
 * Get expiration options for trades
 */
export function getTradeExpirationOptions(): Array<{
  value: TradeExpirationOption;
  label: string;
}> {
  return [
    { value: '1_day', label: '1 Day' },
    { value: '3_days', label: '3 Days' },
    { value: '7_days', label: '7 Days' },
    { value: '30_days', label: '30 Days' },
  ];
}
